import { CAMPAIGN_STATUSES, type CampaignLinkStatus } from "@/constants/campaings";
import type { RedemptionColumnId } from "@/interfaces/reportsRedemptions";

/** The Reports screen's own route — the tab lives in a search param, not a sub-path. */
export const REPORTS_ROUTE_PATH = "/reports";

/** Search param holding the active tab: `useSearchParams`, `replace: true`. */
export const REPORTS_TAB_PARAM = "tab";

/** The three report tabs: Redemptions, Bounces, and Top Gifts. */
export const REPORTS_TABS = [
  { id: "redemptions", label: "Redemptions", enabled: true },
  { id: "bounces", label: "Bounces", enabled: true },
  { id: "topgifts", label: "Top Gifts", enabled: true },
] as const;

/**
 * The API's sort whitelist, mirrored — MUST equal apps/gfgf-api/src/constants/reportsRedemptions.ts
 * `REDEMPTION_SORT_KEYS`, same 18 keys, same order. `giftImg` and `url` have no sort
 * expression and are deliberately excluded.
 */
export const REDEMPTION_SORT_KEYS = [
  "recipientName",
  "recipientEmail",
  "recipientCompany",
  "status",
  "campaignName",
  "senderName",
  "productTitle",
  "giftValue",
  "sentAt",
  "redeemedAt",
  "lastActivity",
  "reminderCount",
  "shippingName",
  "shippingAddress",
  "shippingCity",
  "shippingState",
  "shippingPostal",
  "shippingCountry",
] as const;
export type RedemptionSortKey = (typeof REDEMPTION_SORT_KEYS)[number];

/** Column id -> header label — used for the table header (the server builds the CSV header). */
export const COLUMN_LABELS: Record<RedemptionColumnId, string> = {
  email: "Recipient Email",
  company: "Recipient Company",
  status: "Redemption Status",
  campaign: "Campaign",
  sender: "Sender Name",
  product: "Gift Product Title",
  giftImg: "Gift Image",
  giftValue: "Gift Value",
  sentAt: "Gift Email Sent At",
  redeemedAt: "Gift Redeemed At",
  lastActivity: "Last Activity",
  reminderCount: "Reminder Count",
  shipName: "Shipping Name",
  shipAddr: "Shipping Address",
  shipCity: "Shipping City",
  shipState: "Shipping State",
  shipPostal: "Shipping Postal",
  shipCountry: "Shipping Country",
  url: "Redemption URL",
};

/** The fixed, always-first, never-configurable column. */
export const FIXED_COLUMN_LABEL = "Recipient Name";

/** Default arrangement of the 19 configurable columns, in the ticket's order. */
export const DEFAULT_ORDER: RedemptionColumnId[] = [
  "email",
  "company",
  "status",
  "campaign",
  "sender",
  "product",
  "giftImg",
  "giftValue",
  "sentAt",
  "redeemedAt",
  "lastActivity",
  "reminderCount",
  "shipName",
  "shipAddr",
  "shipCity",
  "shipState",
  "shipPostal",
  "shipCountry",
  "url",
];

/** Hidden by default until the user opts in via the Columns popover. */
export const DEFAULT_HIDDEN: RedemptionColumnId[] = [
  "giftImg",
  "lastActivity",
  "shipName",
  "shipAddr",
  "shipCity",
  "shipState",
  "shipPostal",
  "shipCountry",
  "url",
];

/** URL search param that holds the column layout — no localStorage, no server. */
export const COLS_PARAM = "cols";

/** Rows per page. */
export const PAGE_SIZE = 100;

/** Table minimum width before horizontal scroll. */
export const MIN_WIDTH = 1100;

/** Placeholder the table and the CSV use for a value the row does not have. */
export const EMPTY_CELL = "—";

/** BlotterFilterBar field ids for this screen's four filters. */
export const FILTER_FIELD_IDS = {
  campaign: "campaign",
  status: "status",
  sent: "sent",
  redeemed: "redeemed",
} as const;

/** Labels for the four filter fields above. */
export const FILTER_LABELS = {
  campaign: "Campaign",
  status: "Redemption Status",
  sent: "Date Sent",
  redeemed: "Date Redeemed",
} as const;

/** The screen's user-facing strings, in one place so a wording change is one edit. */
export const REDEMPTIONS_TEXT = {
  searchPlaceholder: "Search and filter redemptions",
  itemNoun: "row",
  emptyMessage: "No redemptions match your search.",
  csvFilename: "redemptions.csv",
  popoverHint: "Recipient Name is always shown.",
  comingSoon: "Coming soon",
  emptyTitle: "No redemptions yet",
  emptySubtitle: "Once a campaign is sent, every recipient lands here.",
  listError: "We couldn't load redemptions. Check your connection and try again.",
  retry: "Retry",
  copyToast: "Redemption link copied to clipboard",
  copyFailed: "Couldn't copy the redemption link — your browser blocked clipboard access.",
  pageTitle: "Reports",
  exportError: "We couldn't export redemptions. Check your connection and try again.",
} as const;

/** The screen's label for wire status `Pending`. */
export const SCHEDULED_LABEL = "Scheduled";

/** Column id -> the API sort key it drives, or null when the column has no sort expression. */
export const COLUMN_TO_SORT_KEY: Record<RedemptionColumnId, RedemptionSortKey | null> = {
  email: "recipientEmail",
  company: "recipientCompany",
  status: "status",
  campaign: "campaignName",
  sender: "senderName",
  product: "productTitle",
  giftImg: null,
  giftValue: "giftValue",
  sentAt: "sentAt",
  redeemedAt: "redeemedAt",
  lastActivity: "lastActivity",
  reminderCount: "reminderCount",
  shipName: "shippingName",
  shipAddr: "shippingAddress",
  shipCity: "shippingCity",
  shipState: "shippingState",
  shipPostal: "shippingPostal",
  shipCountry: "shippingCountry",
  url: null,
};

/** Sort key the fixed Recipient Name column drives — not in `RedemptionColumnId` since it's not configurable. */
export const FIXED_COLUMN_SORT_KEY: RedemptionSortKey = "recipientName";

/** Status filter option order — ticket order; Pending renders as Scheduled. */
export const STATUS_FILTER_ORDER: readonly CampaignLinkStatus[] = [
  CAMPAIGN_STATUSES.LINK_REDEEMED,
  CAMPAIGN_STATUSES.LINK_SENT,
  CAMPAIGN_STATUSES.LINK_BOUNCED,
  CAMPAIGN_STATUSES.LINK_EXPIRED,
  CAMPAIGN_STATUSES.LINK_DECLINED,
  CAMPAIGN_STATUSES.LINK_PENDING,
] as const;
